import { Component } from "../lib/component.ts";

class PageChain extends Component {
  #nextUrl: string | null = null;

  #fetching = false;

  #ready = true;

  #sentinel: HTMLDivElement | null = null;

  #stages: HTMLDivElement[] = [];

  #loadObserver: IntersectionObserver | null = null;

  #themeObserver: IntersectionObserver | null = null;

  #urlObserver: IntersectionObserver | null = null;

  #cullTimeout = 0;

  connectedCallback(): void {
    super.connectedCallback();

    this.#nextUrl = this.dataset.nextUrl || null;

    const initialSegment = this.querySelector(".page-chain__segment");
    if (initialSegment instanceof HTMLDivElement) {
      this.#stages.push(this.#createStage(initialSegment));
    }

    const sentinel = document.createElement("div");
    sentinel.dataset.sentinel = "";
    this.appendChild(sentinel);
    this.#sentinel = sentinel;

    this.#loadObserver = new IntersectionObserver(this.#handleLoadIntersect, {
      rootMargin: `0px 0px ${window.innerHeight * 4}px 0px`,
    });
    this.#loadObserver.observe(sentinel);

    this.#themeObserver = new IntersectionObserver(this.#handleThemeIntersect, {
      rootMargin: "0px 0px -95% 0px",
    });

    this.#urlObserver = new IntersectionObserver(this.#handleUrlIntersect, {
      rootMargin: "-50% 0px -50% 0px",
    });

    if (initialSegment instanceof HTMLDivElement) {
      this.#themeObserver.observe(initialSegment);
      this.#urlObserver.observe(initialSegment);
    }

    window.addEventListener("scroll", this.#handleScroll, {
      signal: this.signal,
      passive: true,
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#loadObserver?.disconnect();
    this.#themeObserver?.disconnect();
    this.#urlObserver?.disconnect();
    clearTimeout(this.#cullTimeout);
  }

  #handleLoadIntersect = (entries: IntersectionObserverEntry[]): void => {
    for (const entry of entries) {
      if (entry.isIntersecting && this.#nextUrl && !this.#fetching && this.#ready) {
        this.#fetchPage(this.#nextUrl);
      }
    }
  };

  #handleThemeIntersect = (entries: IntersectionObserverEntry[]): void => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      if (!(entry.target instanceof HTMLElement)) continue;
      const segment = entry.target;
      const bg = segment.style.getPropertyValue("--background-color");
      if (bg) {
        document.body.style.setProperty("--background-color", bg);
      } else {
        document.body.style.removeProperty("--background-color");
      }
      if (segment.dataset.theme === "dark") {
        document.body.dataset.theme = "dark";
      } else {
        delete document.body.dataset.theme;
      }
    }
  };

  #handleUrlIntersect = (entries: IntersectionObserverEntry[]): void => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      if (!(entry.target instanceof HTMLElement)) continue;
      const segment = entry.target;
      const url = segment.dataset.url;
      const title = segment.dataset.title ?? "";
      if (url && url !== location.pathname) {
        history.replaceState(null, "", url);
        document.title = title;
        this.dispatchEvent(
          new CustomEvent("page-chain:pageview", {
            bubbles: true,
            detail: { url, title },
          }),
        );
      }
    }
  };

  #handleScroll = (): void => {
    clearTimeout(this.#cullTimeout);
    this.#cullTimeout = window.setTimeout(() => this.#cullSpacers(), 2000);
  };

  async #fetchHtml(url: string): Promise<string | null> {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch(url, { signal: this.signal });
        if (res.ok) return res.text();
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return null;
      }
    }
    return null;
  }

  async #fetchPage(url: string): Promise<void> {
    this.#fetching = true;

    try {
      const html = await this.#fetchHtml(url);
      if (!html) {
        this.#disable();
        return;
      }

      const doc = new DOMParser().parseFromString(html, "text/html");
      const remoteChain = doc.querySelector("page-chain");
      const segmentSource = remoteChain ?? doc;
      const segment = segmentSource.querySelector(".page-chain__segment");

      if (!(segment instanceof HTMLDivElement)) {
        this.#disable();
        return;
      }

      const title = doc.querySelector("title")?.textContent ?? "";
      segment.dataset.title = title;

      const stage = this.#createStage(segment);
      this.insertBefore(stage, this.#sentinel);
      this.#stages.push(stage);
      this.#syncHandoffState();

      this.#themeObserver?.observe(segment);
      this.#urlObserver?.observe(segment);

      this.#ready = false;
      this.#gateOnVisible(segment);

      this.#nextUrl =
        remoteChain instanceof HTMLElement ? remoteChain.dataset.nextUrl || null : null;
      this.#cleanupOldSegments();
    } finally {
      this.#fetching = false;
    }
  }

  #disable(): void {
    this.#nextUrl = null;
    this.#loadObserver?.disconnect();
    document.getElementById("page-chain-footer-hide")?.remove();
  }

  #gateOnVisible(segment: HTMLElement): void {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          this.#ready = true;
          observer.disconnect();
        }
      },
      { threshold: 0 },
    );
    observer.observe(segment);
    this.signal.addEventListener("abort", () => observer.disconnect());
  }

  #createStage(segment: HTMLDivElement): HTMLDivElement {
    const existingStage = segment.closest(".page-chain__stage");
    if (existingStage instanceof HTMLDivElement && existingStage.parentElement === this) {
      return existingStage;
    }

    const stage = document.createElement("div");
    stage.className = "page-chain__stage";
    segment.replaceWith(stage);
    stage.appendChild(segment);
    return stage;
  }

  #syncHandoffState(): void {
    for (let i = 0; i < this.#stages.length; i++) {
      const stage = this.#stages[i];
      if (stage.dataset.replaced) continue;
      stage.toggleAttribute("data-incoming", i > 0);
      stage.toggleAttribute("data-handoff", i < this.#stages.length - 1);
    }
  }

  #cleanupOldSegments(): void {
    if (this.#stages.length <= 2) return;

    const currentIndex = this.#findCurrentSegmentIndex();
    for (let i = 0; i < currentIndex - 1; i++) {
      const stage = this.#stages[i];
      if (stage.dataset.replaced) continue;
      const segment = stage.querySelector(".page-chain__segment");
      const height = this.#getStageLayoutHeight(stage);
      const spacer = document.createElement("div");
      spacer.style.height = `${height}px`;
      spacer.dataset.replaced = "true";
      stage.replaceWith(spacer);
      this.#stages[i] = spacer;
      if (segment) {
        this.#themeObserver?.unobserve(segment);
        this.#urlObserver?.unobserve(segment);
      }
    }
  }

  #cullSpacers(): void {
    let count = 0;
    for (let i = 0; i < this.#stages.length; i++) {
      if (!this.#stages[i].dataset.replaced) break;
      count++;
    }
    if (count === 0) return;

    const anchor = this.#stages[count] ?? this.#sentinel;
    const topBefore = anchor?.getBoundingClientRect().top ?? 0;

    for (let i = 0; i < count; i++) {
      this.#stages[i].remove();
    }
    this.#stages.splice(0, count);
    this.#syncHandoffState();

    const topAfter = anchor?.getBoundingClientRect().top ?? 0;
    window.scrollTo(0, window.scrollY + topAfter - topBefore);
  }

  #getStageLayoutHeight(stage: HTMLElement): number {
    const styles = getComputedStyle(stage);
    const marginTop = Number.parseFloat(styles.marginTop) || 0;
    return Math.max(0, stage.offsetHeight + marginTop);
  }

  #findCurrentSegmentIndex(): number {
    const viewportMid = window.innerHeight / 2;
    for (let i = this.#stages.length - 1; i >= 0; i--) {
      const rect = this.#stages[i].getBoundingClientRect();
      if (rect.top <= viewportMid) return i;
    }
    return 0;
  }
}

if (!customElements.get("page-chain")) {
  customElements.define("page-chain", PageChain);
}
